import { useEffect, useRef, useState, type ReactNode } from 'react';
import { wants3D } from '../gate-3d';
import type { PremizeScene } from './scene';

/**
 * The PreMize section's 3D stage: the box opens and its layers lift out as the
 * stage scrolls into view; from 1024px the layers are labelled, and on screens
 * tall enough to hold them too (`SCREENS_QUERY`) the standout features then
 * play on screens above it.
 *
 * Same contract as `Hero3D`: `children` (the HTML version, `PremizeInside`)
 * renders first and never leaves the DOM; `three` is only ever imported
 * dynamically, after load + idle. The stage takes its height when the scene
 * starts mounting (`data-mounting`) and fades in after its first frame
 * (`data-ready`).
 *
 * What the 3D shows is hidden from the HTML version (visually only) through
 * `data-labels` / `data-screens`, set from the options the scene was actually
 * built with — never from a media query of index.css's own, so the two cannot
 * disagree. When the viewport crosses either query (a rotation, a maximised
 * window) the scene is rebuilt with the new options.
 */
const LABELS_QUERY = '(min-width: 1024px)';
/** Where the 3D plays the features itself. Below it the screens would be too
 *  small to read, so the HTML tiles show instead. */
const SCREENS_QUERY = '(min-width: 1024px) and (min-height: 800px)';

function useMedia(query: string): boolean {
  const [match, setMatch] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatch(mq.matches);
    mq.addEventListener('change', on);
    on();
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return match;
}

export function Premize3D({ children }: { children: ReactNode }) {
  const host = useRef<HTMLDivElement | null>(null);
  const stage = useRef<HTMLDivElement | null>(null);
  const mountedOnce = useRef(false);
  // A scene being replaced stays on screen until its successor has drawn.
  const retiring = useRef<PremizeScene | null>(null);
  const [mounting, setMounting] = useState(false);
  const [ready, setReady] = useState(false);
  const [built, setBuilt] = useState<{ labels: boolean; screens: boolean } | null>(null);
  const labels = useMedia(LABELS_QUERY);
  const screens = useMedia(SCREENS_QUERY);

  useEffect(() => {
    if (!wants3D()) return;
    let scene: PremizeScene | null = null;
    let cancelled = false;
    const cleanups: Array<() => void> = [];

    // Opens as the stage scrolls up: 0 with its top at the bottom of the
    // screen, 1 once its middle reaches the middle of the screen.
    const openNow = () => {
      const r = stage.current!.getBoundingClientRect();
      const vh = window.innerHeight;
      return (vh - r.top) / (vh * 0.5 + r.height * 0.5);
    };

    const mount = (create: typeof import('./scene').createPremizeScene) => {
      mountedOnce.current = true;
      setMounting(true);
      scene = create(stage.current!, { screens, labels, open: openNow() }, () => {
        // Swap in one step: what the 3D now shows, and the old scene out.
        setBuilt({ labels, screens });
        setReady(true);
        retiring.current?.dispose();
        retiring.current = null;
      });

      const onScroll = () => scene?.setOpen(openNow());
      const onPointer = (e: PointerEvent) => {
        if (e.pointerType !== 'mouse') return;
        const r = stage.current!.getBoundingClientRect();
        scene?.setPointer(((e.clientX - r.left) / r.width) * 2 - 1, ((e.clientY - r.top) / r.height) * 2 - 1);
      };
      const io = new IntersectionObserver(([e]) => scene?.setVisible(e.isIntersecting), { threshold: 0.1 });
      io.observe(stage.current!);
      const h = host.current!;
      window.addEventListener('scroll', onScroll, { passive: true });
      h.addEventListener('pointermove', onPointer, { passive: true });
      cleanups.push(() => {
        io.disconnect();
        window.removeEventListener('scroll', onScroll);
        h.removeEventListener('pointermove', onPointer);
      });
      onScroll();
    };

    const start = async () => {
      const { createPremizeScene } = await import('./scene');
      if (cancelled || !stage.current || !host.current) return;
      // The stage grows from nothing when it mounts. Never under the reader's
      // eyes: the first time, mount only while the section is still below the
      // screen, and otherwise wait until it is (a reader already here keeps
      // the HTML version, which says the same). Rebuilds keep their height.
      const below = (top: number) => top >= window.innerHeight;
      if (mountedOnce.current || below(host.current.getBoundingClientRect().top)) {
        mount(createPremizeScene);
        return;
      }
      const wait = new IntersectionObserver(([e]) => {
        if (e.isIntersecting || !below(e.boundingClientRect.top)) return;
        wait.disconnect();
        if (!cancelled) mount(createPremizeScene);
      });
      wait.observe(host.current);
      cleanups.push(() => wait.disconnect());
    };

    if (mountedOnce.current) {
      start().catch(() => {});
    } else {
      // After load and when the browser is idle: never compete with the hero.
      const kickoff = () => {
        const idle = (window as Window & { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
        if (idle) idle(() => start().catch(() => {}));
        else window.setTimeout(() => start().catch(() => {}), 200);
      };
      if (document.readyState === 'complete') kickoff();
      else {
        window.addEventListener('load', kickoff, { once: true });
        cleanups.push(() => window.removeEventListener('load', kickoff));
      }
    }

    return () => {
      cancelled = true;
      cleanups.forEach((f) => f());
      // Keep it drawn (and the HTML version hidden) until the next scene's
      // first frame; the unmount effect below disposes it if none follows.
      if (scene) {
        retiring.current?.dispose();
        retiring.current = scene;
      }
    };
  }, [labels, screens]);

  useEffect(
    () => () => {
      retiring.current?.dispose();
      retiring.current = null;
    },
    [],
  );

  return (
    <div
      ref={host}
      className="p3d"
      data-mounting={mounting || undefined}
      data-ready={ready || undefined}
      data-labels={(ready && built?.labels) || undefined}
      data-screens={(ready && built?.screens) || undefined}
    >
      <div ref={stage} className="p3d-stage" aria-hidden />
      <div className="p3d-fallback">{children}</div>
    </div>
  );
}
